import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PulseIcon, SpinnerGapIcon, StarIcon } from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { fetchWatchlistRunners } from "../lib/api";
import { CoinTable } from "../components/CoinTable";
import { ApeWatermark } from "../components/ApeWatermark";
import { useSeo } from "../lib/useSeo";

const NEON = "#00E599";

export default function WatchlistRunners() {
  const clientId = getClientId();
  useSeo({
    title: "⭐🚀 Watchlist Runners — Your Saved Coins Running Now | AlphaApeing",
    description: "A live board of just the coins you saved that are running right now, sorted by momentum — so you catch your picks the moment they move.",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["watchlistRunners", clientId],
    queryFn: () => fetchWatchlistRunners(clientId),
    refetchInterval: 30000,
  });

  const coins = data?.coins || [];
  const total = data?.total ?? 0;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="watchlist-runners-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black" style={{ background: NEON }}>
            <PulseIcon size={24} weight="bold" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            WATCHLIST <span style={{ color: NEON }}>RUNNERS</span>
          </h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Just the coins <span style={{ color: NEON }} className="font-semibold">you saved</span> that are
          <span style={{ color: NEON }} className="font-semibold"> running right now</span> — surging momentum, up on the hour, or bouncing back in a 2nd wind — ranked by momentum.
          Catch your own picks the moment they move. Turn on the <Link to="/alerts" className="text-neon hover:underline">Watchlist Runner ping</Link> to get told automatically.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap border-t border-white/10 pt-4">
        <Link to="/watchlist" data-testid="wr-manage-watchlist" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-neon transition-colors">
          <StarIcon size={14} weight="fill" color="#F5A623" /> Manage watchlist
        </Link>
        {data && (
          <div className="font-mono text-[11px] text-muted-foreground" data-testid="wr-stats">
            <b style={{ color: NEON }}>{coins.length}</b> running · {total} saved
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><SpinnerGapIcon size={32} className="animate-spin" style={{ color: NEON }} /></div>
      ) : total === 0 ? (
        <div className="border border-white/10 bg-card py-20 text-center text-muted-foreground font-mono text-sm px-6 flex flex-col items-center gap-3">
          <StarIcon size={30} color="#F5A623" />
          Your watchlist is empty. Save coins from the Radar (⭐) or the Organic Runners page to track them here.
          <Link to="/" className="text-neon hover:underline">Browse the Radar →</Link>
        </div>
      ) : coins.length === 0 ? (
        <div className="relative overflow-hidden border border-white/10 bg-card py-20 text-center text-muted-foreground font-mono text-sm px-6 flex flex-col items-center gap-3">
          <ApeWatermark opacity={0.05} size="200px" />
          <span className="relative z-10">None of your {total} saved coins are running right now. This board lights up the moment one starts surging, pumps on the hour, or bounces back.</span>
          <Link to="/watchlist" className="relative z-10 text-neon hover:underline">See all saved coins →</Link>
        </div>
      ) : (
        <CoinTable coins={coins} sort="momentum" order="desc" onSort={() => {}} />
      )}

      <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground border-t border-white/5 pt-4">
        <PulseIcon size={13} style={{ color: NEON }} />
        "Running" = surging momentum (run/heating/waking), up on the 1h, or an organic 2nd-wind bounce. Sorted strongest first. Community-flagged scams hidden. Not financial advice.
      </div>
    </div>
  );
}
